// domain/model/ApprovedLimit.js
import { createCap } from './Cap';

/**
 * ApprovedLimit — one approved amount, and the services that draw on it together.
 *
 * THE POOL, NOT THE CEILING. The letter's "Direct services: 400 units" is the limit; "no more than
 * 40 hours per week" is a `Cap` on it.
 *
 * A LOCAL ENTITY, where its neighbors `CoveredService` and `ChargeLine` are value objects. They are
 * keyed on what they reference; a limit's only candidate key is its SERVICE SET, and the same service
 * may legitimately sit on two limits of one letter when the funder scopes it by setting. So it
 * carries identity instead — `limitId`, unique within the aggregate, meaningless outside it, and
 * minted by infrastructure because the domain does no I/O. Minting on read would make two reads of
 * one document disagree about which limit is which.
 *
 * @domainObject entity
 * @identity limitId
 * @invariant At least one service, and at least one cap.
 * @invariant At most one cap per period. Two weekly ceilings cannot both be true and nothing would
 *   arbitrate between them.
 * @invariant Caps are never additive — see Cap. Enforced by there being no total on the limit for
 *   them to be summed into.
 * @invariant An EMPTY `placeOfServiceCodes` means any setting, with no companion boolean that could
 *   disagree with the list. Enumerating every code known today stays honestly different: a POS code
 *   added next year is covered by "any" and not by the enumeration.
 * @typedef {Object} ApprovedLimit
 * @property {string|null} limitId - local identity; minted by infrastructure, never here
 * @property {AuthorizedService[]} services - every service drawing on this one amount
 * @property {string[]} placeOfServiceCodes - keyed on the place-of-service CODE, never on a
 *   `placesOfService` document id: a clinic may hold several records sharing a code ("School A",
 *   "School B"), and the letter scopes the setting rather than the clinic's record of it.
 * @property {import('./Cap').Cap[]} caps - the ceilings on this amount, in the letter's order
 */

/**
 * AuthorizedService — one treatment service drawing on a limit.
 *
 * AN OBJECT RATHER THAN A BARE ID, though it carries one field today: `CoveredService` was a flat
 * list of ids and the per-service overrides had nowhere to go when they arrived. Funder-honored
 * modifiers are the known next occupant.
 *
 * IT REFERENCES A TREATMENT SERVICE AND NOT A CODE. Two treatment services can route to one code, so
 * a limit keyed on the code would authorize a service the funder never named.
 *
 * @domainObject value-object
 * @invariant One entry per treatment service within a limit. Two entries for one service would
 *   give it two override sets with no rule for choosing between them.
 * @typedef {Object} AuthorizedService
 * @property {string|null} treatmentServiceId - the service approved; NOT this entry's own id
 */

/**
 * Builds a limit, freezing the value objects inside it but not the limit — that is an entity, and
 * the amendment flow will restate one.
 *
 * @param {Partial<ApprovedLimit>} [attrs]
 * @returns {ApprovedLimit}
 */
export function createApprovedLimit(attrs = {}) {
  // A non-list reads as empty rather than throwing: this hydrates from storage as well as from a
  // form, and one hand-edited document must not take down the letter it sits in.
  const listOf = (value) => (Array.isArray(value) ? value : []);

  return {
    limitId: attrs.limitId ?? null,
    services: listOf(attrs.services).map((service) => Object.freeze({
      treatmentServiceId: service?.treatmentServiceId ?? null,
    })),
    placeOfServiceCodes: listOf(attrs.placeOfServiceCodes).filter(
      (code) => typeof code === 'string' && code.length > 0,
    ),
    caps: listOf(attrs.caps).map((cap) => createCap(cap ?? {})),
  };
} // createApprovedLimit()

/**
 * Does this limit approve the given treatment service? One half of the presence match, with
 * `admitsSetting` the other.
 *
 * @param {ApprovedLimit} limit
 * @param {string} treatmentServiceId
 * @returns {boolean}
 */
export function coversService(limit, treatmentServiceId) {
  return treatmentServiceId !== null
    && treatmentServiceId !== undefined
    && limit.services.some((service) => service.treatmentServiceId === treatmentServiceId);
} // coversService()

/**
 * Does this limit's setting scope admit a session delivered under the given place-of-service code?
 *
 * AN EMPTY SCOPE ADMITS EVERYTHING, unknown settings included — the only reading of "any setting"
 * that stays true when a clinic adds a POS code we have never seen.
 *
 * A SCOPED LIMIT REFUSES AN UNKNOWN SETTING. The two directions fail differently: refusing produces
 * a hold somebody can see and clear, admitting produces a claim for work the funder scoped
 * elsewhere, which nobody notices.
 *
 * @param {ApprovedLimit} limit
 * @param {string|null} placeOfServiceCode
 * @returns {boolean}
 */
export function admitsSetting(limit, placeOfServiceCode) {
  if (limit.placeOfServiceCodes.length === 0) {
    return true;
  }

  return limit.placeOfServiceCodes.includes(placeOfServiceCode);
} // admitsSetting()

/**
 * Do several services draw on this one amount? The shared-pool case, which the row summary and the
 * picker's guard copy are driven from.
 *
 * @param {ApprovedLimit} limit
 * @returns {boolean}
 */
export function isShared(limit) {
  return limit.services.length > 1;
} // isShared()
